'use client';

import React, { useMemo, useState, useRef } from 'react';
import styled from 'styled-components';
import { theme } from '@/config/theme';
import { CloseIconModal } from '@/components/icons/ModalIcons';
import { TickIcon } from '@/components/icons/TickIcon';
import { HamburgerMenuIcon } from '@/components/icons/HamburgerMenuIcon';
import { Breadcrumbs } from '@/components/ui-components/Breadcrumbs';
import { ChartHamburgerMenu } from '@/components/charts/combined/ChartHamburgerMenu';
import { FunctionalCheckEntry } from '@/types/urlConfig';

interface FunctionalElementChecksGridProps {
  checks: FunctionalCheckEntry[];
  title?: string;
  onClose?: () => void;
}

interface ElementStats {
  element: string;
  totalChecks: number;
  present: number;
  successRate: number;
}

const Container = styled.div`
  background: ${theme.colors.white};
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: 100%;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const RightSectionWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.gray[700]};
  transition: color 0.2s;
  
  &:hover {
    color: ${theme.colors.gray[900]};
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${theme.colors.status.errorAlt};
  transition: opacity 0.2s;
  
  &:hover {
    opacity: 0.7;
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const GridContainer = styled.div`
  padding: 40px 24px 0;
  overflow-x: auto;
  margin-top: auto;
  padding-bottom: 8px;
`;

const GridWrapper = styled.div`
  display: flex;
  min-width: 100%;
`;

const Grid = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  gap: 8px;
  position: relative;
  align-items: start;
  column-gap: 16px;
  padding: 0 14px;
`;

const YAxisLabel = styled.div`
  grid-column: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-right: 12px;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.gray[700]};
  gap: 8px;
  justify-content: center;
`;

const YAxisTicksContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  padding-right: 8px;
  width: fit-content;
`;

const YAxisTitle = styled.div`
  writing-mode: vertical-rl;
  text-orientation: mixed;
  transform: rotate(180deg);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 0;
  font-size: 12px;
  font-weight: 400;
  color: ${theme.colors.gray[900]};
`;

const XAxisLabelsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 14px 20px;
  padding-left: 97px;
  padding-right: 40px;
  gap: 16px;
`;

const XAxisLabel = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 500;
  color: ${theme.colors.gray[700]};
  writing-mode: vertical-rl;
  min-width: 20px;
  transform: rotate(180deg);
  text-orientation: mixed;
  white-space: nowrap;
`;

const GridCell = styled.div<{ isPresent: boolean; isHovered: boolean }>`
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid ${props => props.isPresent ? '#10b981' : '#ef4444'};
  border-radius: 4px;
  background: ${props => props.isPresent ? '#d1fae5' : '#fee2e2'};
  opacity: ${props => props.isHovered ? 0.6 : 1};
  transition: opacity 0.2s;
  position: relative;
`;

const CheckIcon = styled.div`
  color: #10b981;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const XIcon = styled.div`
  color: #ef4444;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: bold;
`;

const Tooltip = styled.div<{ visible: boolean; x: number; y: number }>`
  position: fixed;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: 8px;
  padding: 12px 16px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1000;
  min-width: 250px;
  display: ${props => props.visible ? 'block' : 'none'};
  pointer-events: none;
`;

const TooltipTitle = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.colors.gray[900]};
  margin-bottom: 8px;
`;

const TooltipRowWrapper = styled.div`
  display: flex;
  gap: 24px;
`;

const TooltipRow = styled.div`
  font-size: 12px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  justify-content: center;
  color: ${theme.colors.gray[900]};

  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipValue = styled.div<{ $color: string }>`
  font-weight: 700;
  color: ${props => props.$color || theme.colors.gray[550]};
`;

const TooltipLabel = styled.div`
  font-weight: 400;
`;  

export const FunctionalElementChecksGrid: React.FC<FunctionalElementChecksGridProps> = ({
  checks,
  title = 'FUNCTIONAL ELEMENT CHECKS',
  onClose,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [hoveredElement, setHoveredElement] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  // Get unique element names (columns) - limit to 10
  const elementNames = useMemo(() => {
    const uniqueElements = Array.from(new Set(checks.map(c => c.element_name)));
    return uniqueElements.slice(0, 10);
  }, [checks]);

  // Get the last 10 check instances (grouped by timestamp)
  const checkInstances = useMemo(() => {
    // Group by timestamp (rounded to second) to get distinct check runs
    const instanceMap = new Map<string, FunctionalCheckEntry[]>();
    checks.forEach(check => {
      const timestampKey = check.timestamp.split('.')[0]; // Remove milliseconds
      if (!instanceMap.has(timestampKey)) {
        instanceMap.set(timestampKey, []);
      }
      instanceMap.get(timestampKey)!.push(check);
    });
    
    // Sort by timestamp (newest first), take last 10, and reverse to show oldest first
    const sortedInstances = Array.from(instanceMap.entries())
      .sort((a, b) => b[0].localeCompare(a[0])) // Sort descending (newest first)
      .slice(-10) // Take last 10
      .reverse() // Reverse to show oldest first in array
      .map(([_, checks]) => checks);
    
    return sortedInstances;
  }, [checks]);

  // Create a map: elementName -> checkInstanceIndex -> isPresent
  const elementCheckMap = useMemo(() => {
    const map = new Map<string, Map<number, boolean>>();
    
    elementNames.forEach(elementName => {
      const elementMap = new Map<number, boolean>();
      checkInstances.forEach((instanceChecks, instanceIndex) => {
        const elementCheck = instanceChecks.find(c => c.element_name === elementName);
        elementMap.set(instanceIndex, elementCheck?.is_present ?? false);
      });
      map.set(elementName, elementMap);
    });
    
    return map;
  }, [elementNames, checkInstances]);

  // Calculate stats for each element based on last 10 check instances
  const elementStats = useMemo(() => {
    const stats: Map<string, ElementStats> = new Map();
    
    elementNames.forEach(elementName => {
      // Total is always the number of instances (10)
      const total = checkInstances?.length;
      let present = 0;
      
      // Count how many instances have this element present
      checkInstances.forEach((instanceChecks) => {
        const elementCheck = instanceChecks.find(c => c.element_name === elementName);
        // If element appears in this instance and is present, count it
        if (elementCheck && elementCheck.is_present) {
          present += 1;
        }
        // If element doesn't appear in instance, it's implicitly not present (counted in total but not in present)
      });
      
      const successRate = total > 0 ? (present / total) * 100 : 0;
      
      stats.set(elementName, {
        element: elementName,
        totalChecks: total,
        present,
        successRate: Math.round(successRate * 10) / 10,
      });
    });
    return stats;
  }, [elementNames, checkInstances]);

  const handleElementHover = (elementName: string, event: React.MouseEvent) => {
    setHoveredElement(elementName);
    const rect = event.currentTarget.getBoundingClientRect();
    // Position tooltip to the left of the element
    // Using a fixed width estimate (250px min-width) plus padding
    const tooltipWidth = 250;
    setTooltipPosition({
      x: rect.left - tooltipWidth - 12,
      y: rect.top,
    });
  };

  const handleElementLeave = () => {
    setHoveredElement(null);
  };

  const getSuccessRateColor = (successRate: number): string => {
    if (successRate >= 80) {
      return '#10b981'; // Green - good
    } else if (successRate >= 50) {
      return '#f59e0b'; // Yellow - warning
    } else {
      return '#ef4444'; // Red - critical
    }
  };
  const stats = hoveredElement ? elementStats.get(hoveredElement) : null;

  // Create table data for hamburger menu
  const tableData = useMemo(() => {
    return elementNames.map(elementName => {
      const stats = elementStats.get(elementName);
      
      // Create row with element name and summary stats only
      const row: Record<string, string> = {
        'Element Name': elementName,
      };
      
      // Add summary columns
      if (stats) {
        row['Total Checks'] = String(stats.totalChecks);
        row['Present'] = String(stats.present);
        row['Success Rate'] = `${stats.successRate}%`;
      }
      
      return row;
    });
  }, [elementNames, elementStats]);

  // Create grid layout: [Y-axis label, ...element columns]
  const gridTemplateColumns = `auto repeat(${elementNames?.length}, 1fr)`;

  return (
    <Container ref={containerRef}>
      <Breadcrumbs
        items={[{ label: title }]}
        accentColor={theme.colors.primary.accent}
        rightSection={
          <RightSectionWrapper>
            <ChartHamburgerMenu
              chartTitle={title}
              tableData={tableData}
              chartContainerRef={containerRef}
              fullscreenEnabled={false}
            />
          </RightSectionWrapper>
        }
        style={{ padding: '24px' }}
      />
      
       
            {/* Element columns with check cells */}
      <GridContainer>
        <GridWrapper>
                 {/* Y-axis label with "Checks" title and row numbers */}
            <YAxisLabel>
              <YAxisTitle>Last 10 Checks</YAxisTitle>
             
            </YAxisLabel>
            <YAxisTicksContainer>
              {checkInstances.map((_, index) => {
                // Invert the order: show 10, 9, 8, ... 1
                const displayNumber = checkInstances?.length - index;
                return (
                  <div key={index} style={{ display:'flex',alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '400', color: theme.colors.gray[700], marginBottom: '8px', height: '20px',width: '20px',}}>
                    {displayNumber}
                  </div>
                );
              })}
              </YAxisTicksContainer>

          <Grid>
   
            {elementNames.map((elementName) => {
              const elementCheckStatuses = elementCheckMap.get(elementName) || new Map();
              // When an element is hovered, all OTHER elements should have reduced opacity
              const isHovered = hoveredElement !== null && hoveredElement !== elementName;
              
              return (
                <div key={elementName} style={{ display: 'flex', flexDirection: 'column', gap: '8px', width: 'fit-content' }}>
                  {checkInstances.map((_, instanceIndex) => {
                    const isPresent = elementCheckStatuses.get(instanceIndex) ?? false;
                    
                    return (
                      <GridCell
                        key={`${elementName}-${instanceIndex}`}
                        isPresent={isPresent}
                        isHovered={isHovered}
                        onMouseEnter={(e) => handleElementHover(elementName, e)}
                        onMouseLeave={handleElementLeave}
                      >
                        {isPresent ? (
                          <CheckIcon>
                            <TickIcon size={16} color="#10b981" />
                          </CheckIcon>
                        ) : (
                          <XIcon>×</XIcon>
                        )}
                      </GridCell>
                    );
                  })}
                </div>
              );
            })}

          </Grid>
          
        </GridWrapper>
        
      </GridContainer>



            {/* X-axis labels (column headers) */}
            <XAxisLabelsContainer>
            {elementNames.map((elementName) => {
              // When an element is hovered, all OTHER labels should have reduced opacity
              const isHovered = hoveredElement !== null && hoveredElement !== elementName;
              
              return (
                <XAxisLabel
                  key={`header-${elementName}`}
                  onMouseEnter={(e) => handleElementHover(elementName, e)}
                  onMouseLeave={handleElementLeave}
                  style={{ opacity: isHovered ? 0.6 : 1, transition: 'opacity 0.2s' }}
                >
                  {elementName}
                </XAxisLabel>
              );
            })}
            </XAxisLabelsContainer>
      {/* Tooltip */}
      {stats && (
        <Tooltip visible={hoveredElement !== null} x={tooltipPosition.x} y={tooltipPosition.y}>
          <TooltipTitle>{stats.element}</TooltipTitle>
          <TooltipRowWrapper>
            <TooltipRow>
                
               <TooltipValue $color={theme.colors.gray[550]}> {stats.totalChecks} 
                </TooltipValue>
                <TooltipLabel>Total Checks</TooltipLabel>
                
                </TooltipRow>
            <TooltipRow>

                <TooltipValue $color={getSuccessRateColor(stats.successRate)}> {stats.present} 
                </TooltipValue>
                <TooltipLabel>Present</TooltipLabel>
                
                </TooltipRow>
            <TooltipRow>
                
                <TooltipValue $color={getSuccessRateColor(stats.successRate)}> {stats.successRate}% 
                </TooltipValue>
                <TooltipLabel>Success Rate</TooltipLabel>
                
                </TooltipRow>
          </TooltipRowWrapper>
        </Tooltip>
      )}
    </Container>
  );
};

export default FunctionalElementChecksGrid;

